import { useState } from "react";
import { Cluster, Stack } from "@salesapp/ui";
import type { LoxleyChart as Chart } from "../lib/loxleyApi";

/*
 * Inline SVG in viewBox units, like the dashboard designer's ChartCard: no charting library and no
 * pixel values. Series colours are the eight --color-chart-N tokens, so light and dark both follow
 * the theme. Every chart also opens as a table, for exact numbers and for screen readers.
 */
const TOP = 4;
const BOTTOM = 48;
const LEFT = 13;
const RIGHT = 98;
const PLOT_HEIGHT = BOTTOM - TOP;
const LABEL_CHARS = 9;

function seriesColor(index: number): string {
  return `var(--color-chart-${(index % 8) + 1})`;
}

function formatValue(value: number, unit: string | null): string {
  const options: Intl.NumberFormatOptions = { notation: "compact", maximumFractionDigits: 1 };
  try {
    return new Intl.NumberFormat(undefined, unit ? { ...options, style: "currency", currency: unit } : options)
      .format(value);
  } catch {
    return String(value); // a currency code Intl does not know
  }
}

/** The chart as text, sent back as history so "make it a bar chart" needs no fresh look-up. */
export function chartAsText(chart: Chart): string {
  const series = chart.series.map((item) => `${item.name}: ${item.values.join(", ")}`).join("; ");
  return `[Chart shown: ${chart.title} (${chart.kind}${chart.unit ? `, ${chart.unit}` : ""}). `
    + `Values: ${chart.measure}. Labels: ${chart.labels.join(", ")}. ${series}]`;
}

function short(label: string): string {
  return label.length > LABEL_CHARS ? `${label.slice(0, LABEL_CHARS - 1)}…` : label;
}

function scale(chart: Chart) {
  const stacked = chart.kind === "stacked_bar";
  const tops = chart.labels.map((_, i) =>
    stacked ? chart.series.reduce((sum, item) => sum + Math.max(0, item.values[i] ?? 0), 0)
      : Math.max(0, ...chart.series.map((item) => item.values[i] ?? 0)));
  const bottoms = chart.labels.map((_, i) => Math.min(0, ...chart.series.map((item) => item.values[i] ?? 0)));
  const max = Math.max(...tops, 0);
  const min = Math.min(...bottoms, 0);
  const span = max - min || 1;
  return { max, min, y: (value: number) => BOTTOM - ((value - min) / span) * PLOT_HEIGHT };
}

function Axes({ chart, y, max, min }: { chart: Chart; y: (v: number) => number; max: number; min: number }) {
  // About six labels fit across the panel; with more bars, label every second or third one.
  const every = Math.ceil(chart.labels.length / 6);
  const step = (RIGHT - LEFT) / chart.labels.length;
  return (
    <>
      {[max, (max + min) / 2, min].map((tick) => (
        <g key={tick}>
          <line x1={LEFT} x2={RIGHT} y1={y(tick)} y2={y(tick)} className="loxley-chart-svg__grid" />
          <text x={LEFT - 1} y={y(tick) + 1} textAnchor="end" className="loxley-chart-svg__label">
            {formatValue(tick, chart.unit)}
          </text>
        </g>
      ))}
      <line x1={LEFT} x2={RIGHT} y1={y(0)} y2={y(0)} className="loxley-chart-svg__axis" />
      {chart.labels.map((label, i) => (i % every === 0 ? (
        <text key={label} x={LEFT + step * (i + 0.5)} y={BOTTOM + 5} textAnchor="middle"
          className="loxley-chart-svg__label">{short(label)}</text>
      ) : null))}
    </>
  );
}

function Bars({ chart }: { chart: Chart }) {
  const { y, max, min } = scale(chart);
  const step = (RIGHT - LEFT) / chart.labels.length;
  const stacked = chart.kind === "stacked_bar";
  const width = stacked ? step * 0.7 : (step * 0.8) / chart.series.length;
  return (
    <svg viewBox="0 0 100 56" className="loxley-chart-svg" role="img" aria-label={chart.title}>
      <Axes chart={chart} y={y} max={max} min={min} />
      {chart.labels.map((label, i) => {
        let up = 0;
        let down = 0;
        return chart.series.map((item, s) => {
          const value = item.values[i] ?? 0;
          let from = 0;
          if (stacked) {
            from = value >= 0 ? up : down;
            if (value >= 0) up += value; else down += value;
          }
          const top = y(Math.max(from, from + value));
          const height = Math.abs(y(from) - y(from + value));
          const x = stacked ? LEFT + step * i + (step - width) / 2 : LEFT + step * i + step * 0.1 + width * s;
          return (
            <rect key={`${label}-${item.name}`} x={x} y={top} width={width} height={height}
              style={{ fill: seriesColor(s) }}>
              <title>{`${item.name}, ${label}: ${formatValue(value, chart.unit)}`}</title>
            </rect>
          );
        });
      })}
    </svg>
  );
}

function Lines({ chart }: { chart: Chart }) {
  const { y, max, min } = scale(chart);
  const step = (RIGHT - LEFT) / chart.labels.length;
  const x = (i: number) => LEFT + step * (i + 0.5);
  return (
    <svg viewBox="0 0 100 56" className="loxley-chart-svg" role="img" aria-label={chart.title}>
      <Axes chart={chart} y={y} max={max} min={min} />
      {chart.series.map((item, s) => (
        <g key={item.name}>
          <polyline className="loxley-chart-svg__line" style={{ stroke: seriesColor(s) }}
            points={item.values.map((value, i) => `${x(i)},${y(value)}`).join(" ")} />
          {item.values.map((value, i) => (
            <circle key={chart.labels[i]} cx={x(i)} cy={y(value)} r={0.9} style={{ fill: seriesColor(s) }}>
              <title>{`${item.name}, ${chart.labels[i]}: ${formatValue(value, chart.unit)}`}</title>
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
}

function Pie({ chart }: { chart: Chart }) {
  const values = chart.series[0]?.values ?? [];
  const total = values.reduce((sum, value) => sum + value, 0) || 1;
  let angle = -Math.PI / 2;
  const point = (a: number) => `${50 + 26 * Math.cos(a)},${28 + 26 * Math.sin(a)}`;
  return (
    <svg viewBox="0 0 100 56" className="loxley-chart-svg" role="img" aria-label={chart.title}>
      {values.map((value, i) => {
        const sweep = (value / total) * Math.PI * 2;
        const start = angle;
        angle += sweep;
        const tip = `${chart.labels[i]}: ${formatValue(value, chart.unit)} (${Math.round((value / total) * 100)}%)`;
        if (sweep >= Math.PI * 2 - 1e-6) {
          return <circle key={chart.labels[i]} cx={50} cy={28} r={26} style={{ fill: seriesColor(i) }}><title>{tip}</title></circle>;
        }
        const large = sweep > Math.PI ? 1 : 0;
        return (
          <path key={chart.labels[i]} style={{ fill: seriesColor(i) }}
            d={`M50,28 L${point(start)} A26,26 0 ${large} 1 ${point(angle)} Z`}>
            <title>{tip}</title>
          </path>
        );
      })}
    </svg>
  );
}

function ChartTable({ chart }: { chart: Chart }) {
  return (
    <div className="loxley-reply-table-wrap">
      <table className="loxley-table">
        <thead>
          <tr>
            <th scope="col">{" "}</th>
            {chart.series.map((item) => <th key={item.name} scope="col" className="loxley-num">{item.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {chart.labels.map((label, i) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              {chart.series.map((item) => (
                <td key={item.name} className="loxley-num">{formatValue(item.values[i] ?? 0, chart.unit)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LoxleyChart({ chart }: { chart: Chart }) {
  const [asTable, setAsTable] = useState(false);
  const legend = chart.kind === "pie" ? chart.labels : chart.series.map((item) => item.name);
  return (
    <figure className="loxley-chart">
      <Stack gap={2}>
        <Cluster gap={2} justify="between" align="center">
          <figcaption>
            <span className="loxley-chart-title">{chart.title}</span>
            {chart.measure ? <span className="loxley-chart-measure">{chart.measure}</span> : null}
          </figcaption>
          <button type="button" className="loxley-chart-toggle" onClick={() => setAsTable((value) => !value)}>
            {asTable ? "Show as chart" : "Show as table"}
          </button>
        </Cluster>
        {asTable ? <ChartTable chart={chart} />
          : chart.kind === "pie" ? <Pie chart={chart} />
            : chart.kind === "line" ? <Lines chart={chart} /> : <Bars chart={chart} />}
        {!asTable && (legend.length > 1 || chart.kind === "pie") ? (
          <Cluster gap={3} className="loxley-chart-legend">
            {legend.map((name, i) => (
              <Cluster key={name} gap={1} align="center">
                <span className="loxley-chart-swatch" style={{ backgroundColor: seriesColor(i) }} />
                {name}
              </Cluster>
            ))}
          </Cluster>
        ) : null}
        {chart.caption ? <p className="loxley-chart-caption">{chart.caption}</p> : null}
      </Stack>
    </figure>
  );
}
